/**
 * useInboxData — single source of truth for the InboxView module's data layer.
 *
 * Encapsulates everything the UI needs from Firestore:
 *   - the user's email accounts (live)
 *   - aggregate counts per tab (total / unread / spam / sent / …)
 *   - the paginated list of emails for the currently selected tab
 *
 * The query logic intentionally mirrors the proven behaviour of the classic
 * SmartInbox (warmup mail is filtered out of the regular inbox, counts exclude
 * warmup, pagination is cursor-based) but lives in one isolated, reusable hook
 * instead of being spread across a 2k-line component.
 *
 * It NEVER mutates anything in the classic inbox; it only reads the same
 * Firestore collections.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  onSnapshot,
} from 'firebase/firestore';
import { sortScheduledNewestFirst, toMillis } from '../utils/emailParsing';

// How many docs we pull from Firestore per round trip. Deliberately larger
// than the visible target so a page sprinkled with hidden (warmup) mail still
// yields a full screen of real mail in a single read.
const FETCH_CHUNK = 50;
// How many *visible* rows we aim to gather before returning a page.
const VISIBLE_TARGET = 25;
// Hard cap on round trips per call, so a long stretch of hidden mail at the
// top of the list can never turn one load into a runaway scan. Anything still
// missing is pulled lazily as the user scrolls (infinite scroll).
const MAX_READS_PER_CALL = 6;
// Upper bound on the automatic "keep paging until something is visible" passes
// (see the auto-fill effect). Only a safety valve — with warmup filtered out
// server-side a first page that yields nothing is already the rare case.
const MAX_AUTO_FILL_PASSES = 20;
const SPECIAL_TABS = ['sent', 'replied', 'forwarded', 'deleted', 'scheduled', 'safeguard', 'warmup'];

export const SAFEGUARD_REASONS = [
  'safeguard_rejection',
  'scorer_rejection',
  'generation_error',
  'crm_blocked',
  'reply_received',
  'email_invalid_final',
];

export const isSpecialTab = (tab) => SPECIAL_TABS.includes(tab);

// ── Robust newest-first ordering for outgoing tabs ──────────────────
// The outgoing collections (sent / forwarded) historically stored their time
// inconsistently — a Firestore Timestamp, a raw epoch-ms number, or an ISO
// date string — sometimes within the same collection. Firestore can't order a
// mixed-type field correctly (numbers sort before Timestamps), which pushed
// the newest mail to the BOTTOM. We therefore normalise every candidate field
// to epoch-ms and re-sort these tabs client-side so the most recent item
// always sits on top.
//
// Scheduled sorts by when the user queued the mail (`queuedAt`), newest
// first — a mail scheduled 10 minutes ago sits above one queued yesterday
// even if the older one sends sooner. That tab uses
// `sortScheduledNewestFirst`.

// Priority mirrors what the row actually displays (timestamp || date) first,
// then the scheduled-time fallbacks used by older docs.
const getEmailMillis = (email) => (
  toMillis(email.timestamp)
  ?? toMillis(email.date)
  ?? toMillis(email.originalScheduledTime)
  ?? toMillis(email.scheduledAt)
  ?? toMillis(email.createdAt)
  ?? 0
);

const sortByNewestFirst = (rows) => [...rows].sort((a, b) => getEmailMillis(b) - getEmailMillis(a));

const sortEmailsForTab = (tab, rows) => {
  if (tab === 'scheduled') return sortScheduledNewestFirst(rows);
  if (CLIENT_SORT_TABS.includes(tab)) return sortByNewestFirst(rows);
  return rows;
};

// Tabs whose underlying collections need the client-side re-sort above.
const CLIENT_SORT_TABS = ['sent', 'replied', 'forwarded'];

const emptyCounts = {
  totalRaw: 0, unread: 0, spam: 0, sent: 0,
  replied: 0, deleted: 0, scheduled: 0, safeguard: 0, warmup: 0,
  leads: 0, leadsUnread: 0, bounced: 0, bounceUnread: 0,
};

/**
 * `leadIndexSignal` is never read — it is only a change signal. Pass anything
 * whose identity changes when the user's Leads change (InboxView bumps a
 * counter after "Add to Leads" / "Block") and the lead badge is re-counted; the
 * tab queries themselves read the denormalized `isLeadMail` flag.
 */
export default function useInboxData(userId, selectedTab, leadIndexSignal = null) {
  const db = getFirestore();

  const [accounts, setAccounts] = useState([]);
  const [accountsReady, setAccountsReady] = useState(false);
  const accountsRef = useRef([]);

  const [emails, setEmails] = useState([]);
  const [rawCounts, setRawCounts] = useState(emptyCounts);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  // Cursor state is kept in a ref so loadMore reads the latest without re-binding.
  const cursorRef = useRef(null);

  // Auto-fill passes spent on the current tab (reset on every first-page load).
  const autoFillRef = useRef(0);

  // Monotonic load token. Every fresh first-page load (tab switch / refresh)
  // bumps it; any in-flight request whose token no longer matches is stale and
  // must not touch state. This is what keeps switching tabs (or a quick
  // refresh) from letting a slow previous query overwrite the current tab.
  const loadSeqRef = useRef(0);

  // ── Accounts (live) ──────────────────────────────────────────────
  useEffect(() => {
    setAccountsReady(false);
    if (!userId) return undefined;
    const q = query(
      collection(db, 'world_users', userId, 'emailAccounts'),
      orderBy('email')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        accountsRef.current = next;
        setAccounts(next);
        setAccountsReady(true);
      },
      (err) => {
        console.error('[useInboxData] accounts listener failed:', err);
        setError('Email accounts could not be loaded.');
        setAccountsReady(true);
      }
    );
    return () => unsub();
  }, [userId, db]);

  // ── Counts ───────────────────────────────────────────────────────
  const loadCounts = useCallback(async () => {
    if (!userId) return;
    try {
      const inbox = collection(db, 'world_inbox', userId, 'emails');
      const sent = collection(db, 'world_sent', userId, 'emails');
      const scheduled = collection(db, 'world_scheduled_emails', userId, 'emails');
      const drafts = collection(db, 'world_drafts', userId, 'emails');

      const [
        totalSnap, unreadSnap, spamSnap, deletedSnap,
        warmupSnap, warmupUnreadSnap, bouncedSnap, bounceUnreadSnap,
        sentSnap, repliedSnap, scheduledSnap, safeguardSnap,
      ] = await Promise.all([
        getCountFromServer(query(inbox, where('deleted', '==', false))),
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isRead', '==', false))),
        getCountFromServer(query(inbox, where('isSpam', '==', true), where('deleted', '==', false))),
        getCountFromServer(query(inbox, where('deleted', '==', true))),
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isWarmup', '==', true))),
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isWarmup', '==', true), where('isRead', '==', false))),
        // Equality-only filters (no orderBy) are served by automatic single-field
        // indexes via zig-zag merge join — no composite index needed.
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isBounce', '==', true))),
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isBounce', '==', true), where('isRead', '==', false))),
        getCountFromServer(query(sent)),
        getCountFromServer(query(sent, where('emailType', 'in', ['custom_reply', 'draft_reply']))),
        getCountFromServer(query(scheduled)),
        getCountFromServer(query(drafts, where('status', '==', 'draft'), where('reason', 'in', SAFEGUARD_REASONS))),
      ]);

      const warmup = warmupSnap.data().count;
      const warmupUnread = warmupUnreadSnap.data().count;
      const bounced = bouncedSnap.data().count;
      const bounceUnread = bounceUnreadSnap.data().count;

      // Merge (not replace): `leads` / `leadsUnread` are maintained by the
      // separate lead-scan effect below and must survive a counts refresh.
      setRawCounts((prev) => ({
        ...prev,
        // Raw inbox size. The "All" badge is derived from it further down, once
        // the lead count is in too — see the useMemo below.
        totalRaw: totalSnap.data().count,
        unread: Math.max(0, unreadSnap.data().count - warmupUnread),
        spam: spamSnap.data().count,
        deleted: deletedSnap.data().count,
        warmup,
        bounced,
        bounceUnread,
        sent: sentSnap.data().count,
        replied: repliedSnap.data().count,
        scheduled: scheduledSnap.data().count,
        safeguard: safeguardSnap.data().count,
      }));
    } catch (err) {
      console.error('[useInboxData] count load failed:', err);
    }
  }, [userId, db]);

  // ── Lead counts (cheap server aggregate) ─────────────────────────
  // The inbox "Leads" tab is a split by sender identity — but a backend trigger
  // denormalizes that onto each mail as `isLeadMail`, so we can count it with a
  // 1-read server aggregate, exactly like the other tabs, instead of scanning
  // the whole mailbox client-side. (See functions/inboxLeadFlags.js. Requires a
  // one-time `backfillInboxLeadFlags` run to stamp pre-existing mail.)
  const loadLeadCounts = useCallback(async () => {
    if (!userId) return;
    try {
      const inbox = collection(db, 'world_inbox', userId, 'emails');
      const [leadsSnap, leadsUnreadSnap] = await Promise.all([
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isLeadMail', '==', true))),
        getCountFromServer(query(inbox, where('deleted', '==', false), where('isLeadMail', '==', true), where('isRead', '==', false))),
      ]);
      setRawCounts((prev) => ({
        ...prev,
        leads: leadsSnap.data().count,
        leadsUnread: leadsUnreadSnap.data().count,
      }));
    } catch (err) {
      console.error('[useInboxData] lead count load failed:', err);
    }
  }, [userId, db]);

  // "All" shows everything that is neither warmup, bounce nor lead mail — each
  // of those has its own tab. Deriving the badge from the same four aggregates
  // keeps the number from ever disagreeing with the list below it.
  //
  // `unreadPrimary` is the headline "you have new mail" figure: unread bounce
  // notices are machine noise nobody needs to act on, so they must never inflate
  // it. They stay visible on their own tab badge instead.
  const counts = useMemo(() => ({
    ...rawCounts,
    total: Math.max(
      0,
      rawCounts.totalRaw - rawCounts.warmup - rawCounts.bounced - rawCounts.leads,
    ),
    unreadPrimary: Math.max(0, rawCounts.unread - rawCounts.bounceUnread),
  }), [rawCounts]);

  // ── Query builder per tab ────────────────────────────────────────
  // Returns { build(cursor) -> firestore query, map(doc) -> email, clientFilter }.
  const queryPlanForTab = useCallback((tab) => {
    const inbox = collection(db, 'world_inbox', userId, 'emails');
    const sent = collection(db, 'world_sent', userId, 'emails');
    const forwarded = collection(db, 'world_forwarded', userId, 'emails');
    const scheduled = collection(db, 'world_scheduled_emails', userId, 'emails');
    const drafts = collection(db, 'world_drafts', userId, 'emails');

    switch (tab) {
      case 'sent':
        return {
          base: query(sent, orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isSent: true }),
        };
      case 'replied':
        return {
          base: query(sent, where('emailType', 'in', ['custom_reply', 'draft_reply']), orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isSent: true, isReplied: true }),
        };
      case 'forwarded':
        return {
          base: query(forwarded, orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isForwarded: true }),
        };
      case 'deleted':
        return {
          base: query(inbox, where('deleted', '==', true), orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isDeleted: true }),
        };
      case 'scheduled':
        // Queue time, newest first. `queuedAt` is a Firestore Timestamp
        // written by saveScheduledEmail; client-side we still re-sort via
        // sortScheduledNewestFirst so mixed-type leftovers can't jump a
        // mail the user just scheduled.
        return {
          base: query(scheduled, orderBy('queuedAt', 'desc')),
          decorate: (e) => ({ ...e, isScheduled: true }),
        };
      case 'safeguard':
        return {
          base: query(drafts, where('status', '==', 'draft'), where('reason', 'in', SAFEGUARD_REASONS), orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isSafeGuardDraft: true }),
        };
      case 'warmup':
        return {
          base: query(inbox, where('deleted', '==', false), where('isWarmup', '==', true), orderBy('timestamp', 'desc')),
          decorate: (e) => e,
        };
      case 'all':
        // The main inbox hides lead mail (own "leads" tab) and undeliverable
        // bounce notices (own "bounced" tab).
        //
        // Warmup mail is excluded SERVER-side. It has to be: on a warmed-up
        // account it can make up ~99% of the recent timeline, and a client-side
        // filter can only ever thin out the page it already paid for — the
        // capped scan below then returns zero rows and the inbox looks empty
        // even though thousands of real mails sit further down. `isWarmup` is
        // an explicit boolean on every doc (mail fetchers + inboxLeadFlags
        // trigger + backfill), which is what makes `== false` safe here.
        return {
          base: query(inbox, where('deleted', '==', false), where('isWarmup', '==', false), orderBy('timestamp', 'desc')),
          decorate: (e) => e,
          clientFilter: (e) => e.isBounce !== true && e.isLeadMail !== true,
        };
      case 'leads':
        // The exact mirror image of "all". Both sides read the same denormalized
        // `isLeadMail` flag, so a mail is always in exactly one of the two tabs —
        // mixing the flag here with the live lead index over there left a window
        // right after adding a lead where mail showed up in neither. The flag is
        // kept in sync by functions/inboxLeadFlags.js, so this also matches the
        // badge exactly, and removing a contact from Leads still moves its mail
        // back to "all" (within seconds, once the trigger has run).
        return {
          base: query(inbox, where('deleted', '==', false), where('isLeadMail', '==', true), orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isLeadMail: true }),
        };
      case 'bounced':
        // Mailer-Daemon / delivery-failure notices — the bounce mail "all"
        // hides. Server-side for the same reason as the leads tab.
        return {
          base: query(inbox, where('deleted', '==', false), where('isBounce', '==', true), orderBy('timestamp', 'desc')),
          decorate: (e) => ({ ...e, isBounceMail: true }),
        };
      default:
        // A specific account id.
        return {
          base: query(inbox, where('accountId', '==', tab), where('deleted', '==', false), where('isWarmup', '==', false), orderBy('timestamp', 'desc')),
          decorate: (e) => e,
          clientFilter: (e) => e.isBounce !== true && e.isLeadMail !== true,
        };
    }
  }, [userId, db]);

  // Some tabs filter rows client-side (e.g. warmup mail is hidden from the
  // "all" and per-account views). A single Firestore page can be thinned out —
  // or emptied entirely — by that filter, which previously left the list
  // either showing "No emails" or only a handful of rows even though plenty of
  // matching mail sits further down (this happens right after adding a fresh
  // account whose warmup run floods the newest timestamps).
  //
  // To keep the list feeling full and seamless we pull larger chunks and keep
  // paging until we've gathered a screenful of *visible* rows — but never more
  // than MAX_READS_PER_CALL round trips, so a load can't turn into a runaway
  // scan. Whatever is still missing streams in as the user scrolls. `hasMore`
  // (pageFull) reflects whether the underlying source has more docs.
  const runQuery = useCallback(async (tab, cursor) => {
    const plan = queryPlanForTab(tab);
    let nextCursor = cursor;
    let lastDoc = cursor || null;
    let pageFull = false;
    const collected = [];
    let reads = 0;

    do {
      let q = query(plan.base, limit(FETCH_CHUNK));
      if (nextCursor) q = query(q, startAfter(nextCursor));

      // eslint-disable-next-line no-await-in-loop
      const snap = await getDocs(q);
      const docCount = snap.docs.length;
      pageFull = docCount === FETCH_CHUNK;
      lastDoc = snap.docs[docCount - 1] || lastDoc;
      nextCursor = snap.docs[docCount - 1] || null;

      let rows = snap.docs.map((d) => plan.decorate({ id: d.id, ...d.data() }));
      if (plan.clientFilter) rows = rows.filter(plan.clientFilter);
      collected.push(...rows);

      reads += 1;
      // Keep filling while the source has more, we're short of a screenful,
      // and we haven't hit the read cap.
    } while (pageFull && collected.length < VISIBLE_TARGET && reads < MAX_READS_PER_CALL);

    return { rows: collected, lastDoc, pageFull };
  }, [queryPlanForTab]);

  // ── Initial / tab-change load ────────────────────────────────────
  const loadFirstPage = useCallback(async (tab) => {
    if (!userId) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    cursorRef.current = null;
    autoFillRef.current = 0;
    try {
      const { rows, lastDoc, pageFull } = await runQuery(tab, null);
      if (seq !== loadSeqRef.current) return; // superseded by a newer load
      cursorRef.current = lastDoc;
      setEmails(sortEmailsForTab(tab, rows));
      setHasMore(pageFull);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('[useInboxData] load failed:', err);
      setError('Emails could not be loaded.');
      setEmails([]);
      setHasMore(false);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [userId, runQuery]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursorRef.current) return;
    // Tie this page to the current first-page load: if the user switches tabs
    // (or refreshes) while we're fetching, the token changes and we discard the
    // result instead of appending it to — or paginating — the wrong tab.
    const seq = loadSeqRef.current;
    const tab = selectedTab;
    setLoadingMore(true);
    try {
      const { rows, lastDoc, pageFull } = await runQuery(tab, cursorRef.current);
      if (seq !== loadSeqRef.current) return; // tab switched / refreshed meanwhile
      cursorRef.current = lastDoc;
      setEmails((prev) => {
        return sortEmailsForTab(tab, [...prev, ...rows]);
      });
      setHasMore(pageFull);
    } catch (err) {
      console.error('[useInboxData] loadMore failed:', err);
      // Stop paginating rather than leaving `hasMore` true: the auto-fill below
      // would otherwise re-enter immediately and retry in a tight loop. Refresh
      // (or a tab switch) starts over.
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, selectedTab, runQuery]);

  // Mail queries wait until we know whether a mailbox exists. Otherwise the
  // empty two-pane inbox paints first, then swaps to the connect screen.
  useEffect(() => {
    if (!userId || !accountsReady || accounts.length === 0) return;
    loadFirstPage(selectedTab);
    loadCounts();
  }, [userId, selectedTab, accountsReady, accounts.length, loadFirstPage, loadCounts]);

  // A page can still be thinned out to nothing by the remaining client-side
  // filters. Infinite scroll can't rescue that on its own: with zero rows the
  // list renders its empty state, which has no scroll sentinel, so loadMore is
  // never triggered and the inbox stays permanently on "No emails here" even
  // though `hasMore` is true. Keep paging ourselves until at least one row is
  // visible or the source is exhausted — then hand back over to the sentinel.
  useEffect(() => {
    if (loading || loadingMore || !hasMore || emails.length > 0) return;
    if (autoFillRef.current >= MAX_AUTO_FILL_PASSES) {
      // Give up rather than scan the whole mailbox. Clearing `hasMore` also
      // lets the list drop the skeleton and show its real empty state.
      setHasMore(false);
      return;
    }
    autoFillRef.current += 1;
    loadMore();
  }, [loading, loadingMore, hasMore, emails.length, loadMore]);

  // Recount lead mail when the user or the live lead index changes (e.g. right
  // after "Add to Leads"). It's a cheap aggregate, so re-running it on lead
  // changes is fine — the backend trigger keeps `isLeadMail` in sync, this just
  // re-reads the fresh total.
  useEffect(() => {
    if (!userId || !accountsReady || accounts.length === 0) return;
    loadLeadCounts();
  }, [userId, leadIndexSignal, accountsReady, accounts.length, loadLeadCounts]);

  // ── Optimistic local mutations ───────────────────────────────────
  const removeEmailLocally = useCallback((id) => {
    setEmails((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const patchEmailLocally = useCallback((id, patch) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadFirstPage(selectedTab), loadCounts(), loadLeadCounts()]);
  }, [loadFirstPage, loadCounts, loadLeadCounts, selectedTab]);

  return {
    accounts,
    accountsReady,
    emails,
    counts,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
    removeEmailLocally,
    patchEmailLocally,
  };
}
